/**
 * The completed Agent Hero sandbox architecture, shown once in the final recap:
 *   sandbox boundary (microVM, own Docker engine, shared project inside, host files outside) · network guard on the
 *   single way out, with the owner's policy outside · credential injector at the guard, real secrets in a vault outside ·
 *   dev/browser kit at the agent's workbench.
 * It is a learning aid, not a security guarantee: the shared project is drawn inside the boundary and labeled editable.
 */
import { color, font } from './tokens.ts';

type Props = {
  /** Draw the layers in one after another (off for reduced motion). */
  animate?: boolean;
  title?: string;
  className?: string;
};

const mono = { fontFamily: font.mono } as const;
const ui = { fontFamily: font.ui } as const;

function Label({ x, y, children, anchor = 'start', fill = color.accentSoft, size = 9 }: { x: number; y: number; children: string; anchor?: 'start' | 'middle' | 'end'; fill?: string; size?: number }) {
  return (
    <text x={x} y={y} textAnchor={anchor} fill={fill} style={{ ...mono, fontSize: size, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
      {children}
    </text>
  );
}

function Agent({ x, y, s = 30 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y})`} aria-label="The agent">
      <line x1={0} y1={-s * 0.42} x2={0} y2={-s * 0.58} stroke={color.accentSoft} strokeWidth={2} />
      <circle cx={0} cy={-s * 0.63} r={s * 0.07} fill={color.accentSoft} />
      <rect x={-s * 0.45} y={-s * 0.42} width={s * 0.9} height={s * 0.84} rx={s * 0.26} fill={color.brand} stroke={color.brandBright} strokeWidth={1.5} />
      <rect x={-s * 0.32} y={-s * 0.24} width={s * 0.64} height={s * 0.34} rx={s * 0.14} fill={color.bg} />
      <circle cx={-s * 0.13} cy={-s * 0.07} r={s * 0.055} fill={color.text} />
      <circle cx={s * 0.13} cy={-s * 0.07} r={s * 0.055} fill={color.text} />
    </g>
  );
}

function Key({ x, y, pretend, c }: { x: number; y: number; pretend?: boolean; c: string }) {
  return (
    <g transform={`translate(${x} ${y})`} stroke={c} strokeWidth={1.8} fill="none" strokeLinecap="round" strokeDasharray={pretend ? '2 2.5' : undefined}>
      <circle cx={-5} cy={0} r={4} />
      <path d="M-1 0 H9 V3.5 M5 0 V3" />
    </g>
  );
}

function Chip({ x, y, w, label, stroke = color.line, dashed, fill = color.panel, textFill = color.text }: { x: number; y: number; w: number; label: string; stroke?: string; dashed?: boolean; fill?: string; textFill?: string }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={20} rx={5} fill={fill} stroke={stroke} strokeDasharray={dashed ? '3 2' : undefined} />
      <text x={x + w / 2} y={y + 13.5} textAnchor="middle" fill={textFill} style={{ ...mono, fontSize: 9 }}>
        {label}
      </text>
    </g>
  );
}

export function Blueprint({ animate = false, title = 'Agent Hero sandbox architecture', className }: Props) {
  /** Staggered reveal: boundary → guard → credentials → kit → takeaway. */
  const layer = (i: number) => (animate ? { className: 'bp-new', style: { animationDelay: `${i * 0.45}s` } } : {});
  const desc = [
    'The agent works inside a sandbox boundary, a microVM with its own Docker engine. The shared project is inside the boundary and stays editable; host files such as photos, passwords and other projects stay outside.',
    "The sandbox's single way out passes a network guard outside the microVM, which follows a policy only you can edit: allowed destinations pass, others are blocked.",
    'A credential injector at the guard adds the real credential, kept in a vault outside, only to authorized requests; the agent only ever holds a substitute.',
    'A dev and browser kit at the workbench adds build and test tools, never secrets.',
  ].join(' ');

  return (
    <svg viewBox="0 0 360 470" className={className ?? 'blueprint'} role="img" aria-label={`${title}. ${desc}`}>
      <defs>
        <linearGradient id="bp-route" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0" stopColor={color.brand} />
          <stop offset="1" stopColor={color.accentSoft} />
        </linearGradient>
        <pattern id="bp-dots" width="10" height="10" patternUnits="userSpaceOnUse">
          <circle cx="5" cy="5" r="0.6" fill="rgba(170,196,248,0.18)" />
        </pattern>
      </defs>

      {/* Internet band */}
      <g {...layer(1)}>
        <Label x={12} y={18}>Internet</Label>
        <Chip x={22} y={30} w={74} label="model ✓" stroke={color.ok} />
        <Chip x={104} y={30} w={86} label="github.com ✓" stroke={color.ok} />
        <Chip x={198} y={30} w={70} label="registry ✓" stroke={color.ok} />
        <Chip x={276} y={30} w={74} label="exfil ✕" stroke={color.danger} dashed textFill={color.textAccent} />
      </g>

      {/* Host computer */}
      <rect x={6} y={64} width={348} height={398} rx={14} fill="url(#bp-dots)" stroke={color.line} />
      <Label x={16} y={82}>Your computer (host)</Label>

      {/* Network policy, outside the sandbox */}
      <g {...layer(1)}>
        <rect x={212} y={92} width={136} height={74} rx={8} fill={color.navy} stroke={color.accentSoft} />
        <Label x={222} y={108}>Network policy</Label>
        <text x={222} y={124} fill={color.text} style={{ ...mono, fontSize: 9.5 }}>✓ model</text>
        <text x={222} y={138} fill={color.text} style={{ ...mono, fontSize: 9.5 }}>✓ github.com</text>
        <text x={222} y={152} fill={color.text} style={{ ...mono, fontSize: 9.5 }}>✓ registry</text>
        <text x={340} y={162} textAnchor="end" fill={color.textAccent} style={{ ...ui, fontSize: 8.5 }}>only you can edit</text>
      </g>

      {/* Sandbox boundary */}
      <g {...layer(0)}>
        <path d="M 104 178 H 22 V 448 H 204 V 178 H 140" fill="none" stroke={color.accentSoft} strokeWidth={2.5} className={animate ? 'bp-draw' : undefined} />
        <rect x={22} y={178} width={182} height={270} fill="rgba(29,99,237,0.07)" />
        <Label x={30} y={206}>Sandbox boundary · microVM</Label>
        {/* shared project */}
        <g transform="translate(150 236)">
          <path d="M-11 -14 H5 L11 -8 V14 H-11 Z" fill={color.panel} stroke={color.textAccent} strokeWidth={1.4} />
          <path d="M-6 -2 H6 M-6 4 H6" stroke={color.textAccent} strokeWidth={1.2} />
        </g>
        <text x={150} y={266} textAnchor="middle" fill={color.text} style={{ ...ui, fontSize: 10, fontWeight: 600 }}>shared project</text>
        <text x={150} y={278} textAnchor="middle" fill={color.warn} style={{ ...mono, fontSize: 8.5 }}>inside · editable</text>
        {/* own engine */}
        <g transform="translate(150 318)">
          <path d="M0 -13 L12 -6 V7 L0 14 L-12 7 V-6 Z M-12 -6 L0 1 L12 -6 M0 1 V14" fill="none" stroke={color.accentSoft} strokeWidth={1.4} />
        </g>
        <text x={150} y={346} textAnchor="middle" fill={color.textAccent} style={{ ...mono, fontSize: 8.5 }}>own Docker engine</text>

        {/* Host-only files, outside */}
        {[
          { y: 196, label: 'photos' },
          { y: 226, label: 'passwords' },
          { y: 256, label: 'other projects' },
        ].map((h) => (
          <g key={h.label}>
            <rect x={216} y={h.y} width={132} height={24} rx={6} fill={color.bgDeep} stroke={color.line} />
            <text x={226} y={h.y + 16} fill={color.textAccent} style={{ ...ui, fontSize: 10 }}>{h.label}</text>
            <text x={340} y={h.y + 16} textAnchor="end" fill={color.textAccent} style={{ ...mono, fontSize: 8 }}>host only</text>
          </g>
        ))}
      </g>

      {/* Network guard on the single way out */}
      <g {...layer(1)}>
        <path d="M122 176 V 62" stroke="url(#bp-route)" strokeWidth={2} className={animate ? 'bp-draw' : undefined} />
        <path d="M122 62 L 59 50 M122 62 L147 50" stroke={color.accentSoft} strokeWidth={1} opacity={0.6} />
        <g transform="translate(122 178)">
          <rect x={-18} y={-12} width={36} height={24} rx={6} fill={color.navy} stroke={color.ok} strokeWidth={1.6} />
          <path d="M0 -8 L7 -5 L6 2 L0 8 L-6 2 L-7 -5 Z" fill="none" stroke={color.ok} strokeWidth={1.4} />
        </g>
        <Label x={98} y={170} anchor="end" fill={color.ok}>Network guard</Label>
        <path d="M212 128 C 180 128, 160 160, 142 172" stroke={color.accentSoft} strokeDasharray="2 3" fill="none" />
      </g>

      {/* Agent with a substitute credential */}
      <g>
        <Agent x={70} y={250} s={38} />
        <g {...layer(2)}>
          <rect x={48} y={276} width={46} height={18} rx={9} fill={color.panel} stroke={color.accentSoft} strokeDasharray="3 2" />
          <Key x={62} y={285} pretend c={color.accentSoft} />
          <text x={84} y={289} textAnchor="middle" fill={color.textAccent} style={{ ...mono, fontSize: 7.5 }}>sub</text>
          <text x={71} y={308} textAnchor="middle" fill={color.textAccent} style={{ ...mono, fontSize: 8 }}>substitute only</text>
        </g>
      </g>

      {/* Secret vault + credential injector at the guard */}
      <g {...layer(2)}>
        <rect x={216} y={296} width={132} height={54} rx={8} fill={color.panel} stroke={color.text} strokeWidth={1.3} />
        <Label x={226} y={312} fill={color.text}>Secret vault</Label>
        <Key x={234} y={330} c={color.warn} />
        <text x={250} y={333} fill={color.text} style={{ ...ui, fontSize: 9.5 }}>real keys stay here</text>
        <path d="M348 312 H 351 V 181 H 342" stroke={color.warn} strokeWidth={1.4} strokeDasharray="4 3" fill="none" />
        {/* The injector sits at the guard, outside the boundary. */}
        <path d="M222 181 H 142" stroke={color.warn} strokeWidth={1} strokeDasharray="2 2" fill="none" />
        <g transform="translate(282 181)">
          <rect x={-60} y={-10} width={120} height={20} rx={10} fill={color.bg} stroke={color.warn} />
          <text x={0} y={3.5} textAnchor="middle" fill={color.warn} style={{ ...mono, fontSize: 8.5 }}>credential injector</text>
        </g>
      </g>

      {/* Dev/browser kit at the workbench, inside */}
      <g {...layer(3)}>
        <Label x={30} y={378}>Dev / browser kit</Label>
        <Chip x={30} y={386} w={52} label="build" stroke={color.brand} />
        <Chip x={86} y={386} w={50} label="test" stroke={color.brand} />
        <Chip x={140} y={386} w={58} label="browser" stroke={color.brand} />
        <text x={30} y={426} fill={color.textAccent} style={{ ...ui, fontSize: 8.5 }}>tools only · no secrets inside</text>
      </g>

      <g {...layer(4)}>
        <rect x={216} y={372} width={132} height={76} rx={10} fill={color.brand} stroke={color.brandBright} />
        <text x={282} y={394} textAnchor="middle" fill="#fff" style={{ ...mono, fontSize: 9, letterSpacing: '0.08em' }}>AGENT AT WORK</text>
        <text x={282} y={414} textAnchor="middle" fill="#fff" style={{ ...ui, fontSize: 12.5, fontWeight: 700 }}>build ✓ test ✓</text>
        <text x={282} y={434} textAnchor="middle" fill="#fff" style={{ ...ui, fontSize: 12.5, fontWeight: 700 }}>push ✓</text>
      </g>
    </svg>
  );
}
